import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Activity,
  Filter,
  Loader2,
  User,
  FileText,
  Building2,
  CreditCard,
  Trash2,
  Plus,
  Edit,
  Eye,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Shield,
  Users,
  Leaf,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

const ActivityLogPage = () => {
  const navigate = useNavigate();
  const {
    currentOrg,
    user,
    isAdmin,
    canPerform
  } = useOutletContext();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    resourceType: 'all',
    action: 'all'
  });
  const [exporting, setExporting] = useState(false);

  const pageSize = 20;

  useEffect(() => {
    if (currentOrg && !canPerform('SETTINGS_ACTIVITY_LOG') && !isAdmin) {
      toast.error('Only organization owners and admins can view the activity log');
      navigate('/dashboard');
    }
  }, [currentOrg, canPerform, isAdmin, navigate]);

  // Fetch activity log - direct Supabase query
  const fetchActivityLog = useCallback(async () => {
    if (!currentOrg?.id) return;

    setLoading(true);
    try {
      // Build query
      let query = supabase
        .from('audit_logs')
        .select('*', { count: 'exact' })
        .eq('organisation_id', currentOrg.id)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.resourceType !== 'all') {
        query = query.eq('resource_type', filters.resourceType);
      }
      if (filters.action !== 'all') {
        query = query.eq('action', filters.action);
      }

      // Pagination
      const offset = (currentPage - 1) * pageSize;
      query = query.range(offset, offset + pageSize - 1);

      const { data, count, error } = await query;

      if (error) throw error;

      // Fetch user emails for the logs
      const userIds = [...new Set((data || []).map(log => log.user_id).filter(Boolean))];
      let userEmails = {};

      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, email')
          .in('id', userIds);

        if (users) {
          userEmails = users.reduce((acc, u) => ({ ...acc, [u.id]: u.email }), {});
        }
      }

      // Add user emails to logs
      const logsWithEmails = (data || []).map(log => ({
        ...log,
        user_email: userEmails[log.user_id] || 'System'
      }));

      setLogs(logsWithEmails);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching activity log:', error);
      toast.error('Failed to load activity log');
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.id, currentPage, filters]);

  useEffect(() => {
    if (currentOrg) {
      fetchActivityLog();
    }
  }, [currentOrg, currentPage, filters, fetchActivityLog]);

  // Export activity log to CSV - direct Supabase query
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Build query for all logs
      let query = supabase
        .from('audit_logs')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('created_at', { ascending: false })
        .limit(10000);

      if (filters.resourceType !== 'all') {
        query = query.eq('resource_type', filters.resourceType);
      }
      if (filters.action !== 'all') {
        query = query.eq('action', filters.action);
      }

      const { data: allLogs, error } = await query;

      if (error) throw error;

      if (!allLogs || allLogs.length === 0) {
        toast.error('No activity logs to export');
        return;
      }

      // Fetch user emails
      const userIds = [...new Set(allLogs.map(log => log.user_id).filter(Boolean))];
      let userEmails = {};

      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, email')
          .in('id', userIds);

        if (users) {
          userEmails = users.reduce((acc, u) => ({ ...acc, [u.id]: u.email }), {});
        }
      }

      // Transform data for CSV
      const csvData = allLogs.map(log => ({
        'Date/Time': new Date(log.created_at).toLocaleString('en-AU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }),
        'User': userEmails[log.user_id] || 'System',
        'Action': log.action || '',
        'Resource Type': log.resource_type || '',
        'Description': (log.description || '').replace(/\n/g, ' ')
      }));

      // Generate CSV using papaparse
      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `activity-log-${currentOrg.name?.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);

      toast.success(`Exported ${allLogs.length} activity log entries`);
    } catch (error) {
      console.error('Error exporting activity log:', error);
      toast.error('Failed to export activity log');
    } finally {
      setExporting(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create':
        return <Plus className="w-4 h-4 text-emerald-500" />;
      case 'update':
        return <Edit className="w-4 h-4 text-blue-500" />;
      case 'delete':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'view':
        return <Eye className="w-4 h-4 text-slate-500" />;
      default:
        return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  const getResourceIcon = (resourceType) => {
    switch (resourceType) {
      case 'emission':
        return <FileText className="w-4 h-4 text-red-500" />;
      case 'offset':
        return <Leaf className="w-4 h-4 text-emerald-500" />;
      case 'document':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'organisation':
        return <Building2 className="w-4 h-4 text-purple-500" />;
      case 'subscription':
        return <CreditCard className="w-4 h-4 text-amber-500" />;
      case 'team_member':
        return <Users className="w-4 h-4 text-indigo-500" />;
      case 'user':
        return <User className="w-4 h-4 text-slate-500" />;
      default:
        return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'create':
        return 'bg-emerald-100 text-emerald-700';
      case 'update':
        return 'bg-blue-100 text-blue-700';
      case 'delete':
        return 'bg-red-100 text-red-700';
      case 'view':
        return 'bg-slate-100 text-slate-700';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#003B5C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Activity className="w-7 h-7 mr-3 text-[#74A57F]" />
              Team Activity Log
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Track all changes and actions
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              disabled={exporting || loading || logs.length === 0}
              className="text-slate-600"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={fetchActivityLog}
              disabled={loading}
              className="text-slate-600"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800">Activity Audit Trail</h3>
            <p className="text-sm text-blue-600 mt-1">
              This log tracks all changes made by team members to your organization's data, including emissions entries, documents, and settings.
              Only organization owners and full access members can view this log.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters:</span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-500">Type:</span>
              <Select
                value={filters.resourceType}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, resourceType: value }));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="emission">Emissions</SelectItem>
                  <SelectItem value="offset">Offsets</SelectItem>
                  <SelectItem value="document">Documents</SelectItem>
                  <SelectItem value="organisation">Organisation</SelectItem>
                  <SelectItem value="team_member">Team Members</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-500">Action:</span>
              <Select
                value={filters.action}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, action: value }));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="create">Created</SelectItem>
                  <SelectItem value="update">Updated</SelectItem>
                  <SelectItem value="delete">Deleted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="ml-auto text-sm text-slate-500">
              {totalCount} total {totalCount === 1 ? 'entry' : 'entries'}
            </div>
          </div>
        </div>

        {/* Activity Log Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#003B5C] animate-spin" />
              <span className="ml-3 text-slate-600">Loading activity log...</span>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <Activity className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No activity found</p>
              <p className="text-sm mt-1">Activity will appear here as your team makes changes</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      When
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-900">{formatDate(log.created_at)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-900">
                              {log.user_email || 'System'}
                            </div>
                            {log.is_admin_action && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                <Shield className="w-3 h-3 mr-1" />
                                Admin
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getActionIcon(log.action)}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getActionBadgeColor(log.action)}`}>
                            {log.action}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getResourceIcon(log.resource_type)}
                          <span className="text-sm text-slate-700 capitalize">
                            {log.resource_type?.replace('_', ' ') || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-600 max-w-md truncate">
                          {log.description || '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                  <div className="text-sm text-slate-500">
                    Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLogPage;
